import React, { useEffect, useMemo, useState } from "react";
import { Resource, ResourceAccessLabels } from "../../../types/Resource";
import {
  AiOutlineCloudDownload,
  AiOutlineCloudUpload,
  AiOutlineDownload,
  AiOutlineLink,
  AiOutlineSearch,
} from "react-icons/ai";
import UpdateHistory from "./UpdateHistory";
import useResourceApi from "../hooks/useResourceApi";
import { FaSpinner } from "react-icons/fa";

interface ResourceDetailsProps {
  resource: Partial<Resource>;
  userRole: "admin-full" | "admin-limited";
  onEdit: () => void;
  onDelete: () => void;
}

// ---- Utilidad local: si es “scrapeable”
const isScrapeable = (accessType?: string) =>
  accessType === "direct-link" ||
  accessType === "site-link-search" ||
  accessType === "site-row-search";

// Ícono + etiqueta por tipo de acceso
const getAccessTypeInfo = (accessType: string | undefined) => {
  if (!accessType)
    return { icon: null as React.ReactNode, label: "Desconocido" };

  switch (accessType) {
    case "manual-input":
      return {
        icon: <AiOutlineCloudUpload size={20} className="text-blue-500" />,
        label: ResourceAccessLabels[accessType].labelShort,
      };
    case "direct-link":
      return {
        icon: <AiOutlineLink size={20} className="text-green-500" />,
        label: ResourceAccessLabels[accessType].labelShort,
      };
    case "site-link-search":
    case "site-row-search":
      return {
        icon: <AiOutlineSearch size={20} className="text-yellow-500" />,
        label: ResourceAccessLabels[accessType].labelShort,
      };
    default:
      return { icon: null as React.ReactNode, label: "Desconocido" };
  }
};

type NoticeKind = "success" | "warning" | "error" | "info";

const ResourceDetails: React.FC<ResourceDetailsProps> = ({
  resource,
  userRole,
  onEdit,
  onDelete,
}) => {
  const { logs, isLoading, fetchLogs, downloadFile, forceScrape } =
    useResourceApi();
  const { icon, label } = getAccessTypeInfo(resource.access_type);

  // ---- Toast/alert local (sin libs)
  const [notice, setNotice] = useState<{
    msg: string;
    kind: NoticeKind;
  } | null>(null);
  const showNotice = (msg: string, kind: NoticeKind = "info", ms = 4000) => {
    setNotice({ msg, kind });
    if (ms > 0) window.setTimeout(() => setNotice(null), ms);
  };

  // ---- Estado “Forzar scraping”
  const [scraping, setScraping] = useState(false);
  const [cooldown, setCooldown] = useState<number>(0);

  // ✅ cooldown: prioriza id (estable). Fallback a key si no hay id.
  const cooldownKey = useMemo(() => {
    if (resource.id != null) return `scrape-cooldown-id-${resource.id}`;
    if (resource.resource_key)
      return `scrape-cooldown-key-${resource.resource_key}`;
    return "";
  }, [resource.id, resource.resource_key]);

  // Restaurar cooldown persistido
  useEffect(() => {
    if (!cooldownKey) return;
    const until = Number(localStorage.getItem(cooldownKey) || 0);
    const now = Math.floor(Date.now() / 1000);
    setCooldown(until > now ? until - now : 0);
  }, [cooldownKey]);

  // Tick del cooldown
  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((s) => (s > 1 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Cargar logs iniciales (endpoint actual usa resource_key)
  useEffect(() => {
    if (resource.resource_key) fetchLogs(resource.resource_key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.resource_key]);

  const handleDownload = async () => {
    if (resource.resource_key && resource.name) {
      try {
        await downloadFile(resource.resource_key, resource.name);
      } catch {
        showNotice("No se pudo descargar el archivo.", "error");
      }
    }
  };

  // ✅ Forzar scraping por ID (backend oficial by id). Mantiene fetchLogs por key.
  const handleForceScrape = async () => {
    if (resource.id == null) {
      showNotice(
        "No se puede forzar scraping: falta ID del recurso.",
        "error",
        5000
      );
      return;
    }
    setScraping(true);
    try {
      const resp = await forceScrape(resource.id);

      switch (resp.kind) {
        case "accepted": {
          if (resource.resource_key) fetchLogs(resource.resource_key);

          showNotice(
            resp.data?.message ||
              "Scraping solicitado correctamente. 🕓 Puede tardar unos minutos en verse reflejado en el historial o en los datos.",
            "success",
            8000 // ⏱️ más tiempo en pantalla
          );

          break;
        }
        case "cooldown": {
          // obtener cooldown seguro
          let cd =
            typeof resp.cooldown === "number"
              ? resp.cooldown
              : Number(
                  (resp as any)?.data?.retry_after ??
                    (resp as any)?.data?.cooldown_seconds ??
                    300
                );

          if (!Number.isFinite(cd) || cd < 0) cd = 300;

          // convertir a minutos:segundos
          const minutes = Math.floor(cd / 60);
          const seconds = cd % 60;
          const formatted =
            minutes > 0
              ? `${minutes} min ${seconds.toString().padStart(2, "0")} s`
              : `${seconds} s`;

          const now = Math.floor(Date.now() / 1000);
          if (cooldownKey) localStorage.setItem(cooldownKey, String(now + cd));

          setCooldown(cd);
          showNotice(
            `Ya hay una solicitud reciente. Se puede dar unos minutos en verse reflejado. 
Vuelve a intentarlo en aproximadamente ${formatted}.`,
            "warning",
            8000 // ⏱️ más tiempo visible
          );
          break;
        }
        case "error": {
          const msg =
            resp.data?.message ||
            resp.data?.error ||
            "No se pudo procesar la solicitud.";
          showNotice(msg, "error", 5000);
          break;
        }
      }
    } catch {
      showNotice(
        "Fallo de red: no se pudo contactar al servidor.",
        "error",
        5000
      );
    } finally {
      setScraping(false);
    }
  };

  const canScrape = isScrapeable(resource.access_type);
  const disableScrape = useMemo(
    () => scraping || isLoading || cooldown > 0,
    [scraping, isLoading, cooldown]
  );

  // estilos del banner según tipo
  const noticeCls =
    notice?.kind === "success"
      ? "bg-green-50 text-green-800 border-green-300"
      : notice?.kind === "warning"
      ? "bg-amber-50 text-amber-800 border-amber-300"
      : notice?.kind === "error"
      ? "bg-red-50 text-red-800 border-red-300"
      : "bg-slate-50 text-slate-800 border-slate-300";

  return (
    <div className="resource-details flex portrait:flex-col h-full landscape:space-x-8">
      <div className="flex-1 p-6 landscape:overflow-auto portrait:h-min flex flex-col justify-between select-text">
        <div>
          <div className="mb-4">
            <h3 className="text-2xl font-bold">
              {resource.name || "Sin título"}
            </h3>
            <div className="text-sm text-gray-500 mb-4 italic">
              {/* Muestra key como referencia, pero el control es por id */}
              <span>{resource.resource_key}</span>
            </div>
            <div className="flex items-center text-sm text-gray-500 mt-1 space-x-2">
              {icon}
              <span>{label}</span>
            </div>
          </div>

          {resource.url && (
            <p className="text-sm mb-4">
              <strong>URL:</strong>{" "}
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 underline"
              >
                {resource.url}
              </a>
            </p>
          )}
          {resource.search_term && (
            <p className="text-sm">
              <strong>Término de Búsqueda:</strong> {resource.search_term}
            </p>
          )}
        </div>

        <div className="mt-6 space-y-4 ">
          {/* Banner de notificación */}
          {notice && (
            <div className={`mb-4 border rounded px-3 py-2 ${noticeCls}`}>
              {notice.msg}
            </div>
          )}

          <div>
            {/* Forzar scraping */}
            {canScrape && (
              <button
                className={`py-2 px-4 rounded-full transition flex items-center font-medium ${
                  disableScrape
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : scraping
                    ? "bg-orange-500 hover:bg-orange-600 text-white"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white"
                }`}
                onClick={handleForceScrape}
                disabled={disableScrape}
                title={
                  cooldown > 0
                    ? `Disponible en ${cooldown}s`
                    : "Forzar scraping (se ejecuta en background)"
                }
              >
                {scraping ? (
                  <FaSpinner className="animate-spin mr-2" size={18} />
                ) : (
                  <AiOutlineCloudDownload className="mr-2" size={20} />
                )}
                {scraping
                  ? "Ejecutando..."
                  : cooldown > 0
                  ? `Esperar ${cooldown}s`
                  : "Forzar scraping"}
              </button>
            )}
          </div>

          <div className="flex space-x-4 justify-start items-center">
            <button
              className="text-white py-2 px-4 rounded-full bg-black hover:bg-gray-800 transition"
              onClick={onEdit}
            >
              Editar
            </button>

            {userRole === "admin-full" && (
              <button
                className="bg-red-500 text-white py-2 px-4 rounded-full hover:bg-red-600 transition"
                onClick={onDelete}
              >
                Eliminar
              </button>
            )}

            {/* Descargar */}
            <button
              className="bg-blue-500 text-white py-2 px-4 rounded-full hover:bg-blue-600 transition flex items-center"
              onClick={handleDownload}
              disabled={isLoading}
            >
              {isLoading ? (
                <FaSpinner className="animate-spin mr-2" size={20} />
              ) : (
                <AiOutlineDownload className="mr-2" size={20} />
              )}
              {isLoading ? "Descargando..." : "Descargar"}
            </button>
          </div>
        </div>
      </div>

      <div className="w-px portrait:hidden bg-gray-300"></div>

      <UpdateHistory
        updates={logs}
        fetchLogs={() =>
          resource.resource_key && fetchLogs(resource.resource_key)
        }
      />
    </div>
  );
};

export default ResourceDetails;
